import moment from 'moment';
import find from 'lodash/find';

/**
 * @typedef {Object} Holiday
 * @property {string} month_day - Date au format MM-DD
 * @property {string} year - Année ou '0000' pour générique
 * @property {string} sectors_geo_chain - Liste des secteurs sous forme de chaîne ex: "/id1/id2/"
 */

/**
 * @typedef {Object} Shift
 * @property {string|null} [resource_id] - Identifiant de la ressource (agent), ou null
 * @property {string|null} [site_sector_geo_id] - Secteur géographique du site (vacation)
 * @property {string|null} [unavailability_id] - Présent si shift = indisponibilité
 */

/**
 * Vérifie si une date correspond à un jour férié selon différents cas:
 * - Cas 0: Pas un jour férié
 * - Cas 1: Jour sans vacation/agent ou indisponibilité - affiche le jour férié si sans secteur géo ou lié au secteur géo de l'agent/site
 * - Cas 2: Jour férié - affiche si lié au secteur géo du site de la vacation
 *
 * @param {Object} params - Paramètres de la fonction
 * @param {Holiday[]} params.holidays - Liste des jours fériés
 * @param {string} params.date - Date à vérifier (format YYYY-MM-DD)
 * @param {string|null} [params.sectorGeoId=null] - ID du secteur géographique lié au contrat de l'agent ou au site
 * @param {Shift[]} [params.shifts=[]] - Liste des shifts/vacations
 * @param {boolean} [params.isGeneralScheduleSite=false] - Indique si le site est un planning site général (90% du temps false)
 * @returns {boolean} true si la date est un jour férié applicable, false sinon
 */
function isHolidayDate({ holidays, date, sectorGeoId = null, shifts = [], isGeneralScheduleSite = false }) {
  const dateMoment = moment(date, 'YYYY-MM-DD');
  // Sécurisation du format de date reçue
  const holiday = find(
    holidays,
    // Cas du planning généré du client => les jours férié sont rapatriés sans distinction d'années car on ignore sur combien de temps va porter le devis
    (holiday) =>
      holiday.month_day === dateMoment.format('MM-DD') &&
      (holiday.year === dateMoment.format('YYYY') || holiday.year === '0000')
  );

  // Ceci n'est pas un jour férié, on s'arrête là
  if (!holiday?.month_day) {
    return false;
  }

  // Jour sans vacation OU vacation sans agent OU indisponibilité cas général
  if (
    !shifts?.length ||
    shifts.every((shift) => !shift.resource_id) ||
    shifts.some((shift) => shift.unavailability_id)
  ) {
    return Boolean(
      !holiday.sectors_geo_chain.length ||
        (!isGeneralScheduleSite && sectorGeoId && holiday.sectors_geo_chain.includes(`/${sectorGeoId}/`))
    );
  } else {
    return Boolean(
      !holiday.sectors_geo_chain.length ||
        shifts.some(
          (shift) => !!shift.site_sector_geo_id && holiday.sectors_geo_chain.includes(`/${shift.site_sector_geo_id}/`)
        )
    );
  }
}

export default isHolidayDate;
